import { useState } from 'react';
import { getStoredUser } from '../../lib/auth.js';

const tabs = ['Profile', 'Team', 'Integrations', 'Billing', 'Sync Status'];

const mockIntegrations = [
  { name: 'Google Ads', icon: '📊', status: 'connected', accounts: 12, lastSync: '5 min ago' },
  { name: 'Ringba', icon: '📞', status: 'connected', accounts: 1, lastSync: '1 min ago' },
  { name: 'AssemblyAI', icon: '🎙️', status: 'connected', accounts: 1, lastSync: '3 min ago' },
  { name: 'Telnyx', icon: '☎️', status: 'connected', accounts: 1, lastSync: '10 min ago' },
  { name: 'Stripe', icon: '💳', status: 'not_configured', accounts: 0, lastSync: null },
  { name: 'Meta Ads', icon: '📱', status: 'connected', accounts: 1, lastSync: '15 min ago' },
];

const mockSyncJobs = [
  { name: 'Ringba Call Sync', schedule: 'Every 60s', lastRun: '30s ago', status: 'healthy' },
  { name: 'Google Ads Hourly', schedule: 'Every 10 min', lastRun: '5m ago', status: 'healthy' },
  { name: 'Google Ads Keywords', schedule: 'Every 10 min', lastRun: '5m ago', status: 'healthy' },
  { name: 'Transcription Queue', schedule: 'Continuous', lastRun: '2m ago', status: 'healthy' },
  { name: 'Classification Queue', schedule: 'Continuous', lastRun: '2m ago', status: 'healthy' },
  { name: 'Guardian Eval', schedule: 'Every 10 min', lastRun: '8m ago', status: 'healthy' },
  { name: 'ROAS Snapshots', schedule: 'Every hour', lastRun: '45m ago', status: 'healthy' },
];

function StatusDot({ status }: { status: string }) {
  const colors: Record<string, string> = {
    connected: 'bg-emerald-500',
    healthy: 'bg-emerald-500',
    not_configured: 'bg-slate-500',
    error: 'bg-rose-500',
    degraded: 'bg-amber-500',
  };
  return <div className={`w-2 h-2 rounded-full ${colors[status] ?? 'bg-slate-500'}`} />;
}

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState('Profile');
  const user = getStoredUser();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">⚙️ Settings</h1>

      <div className="overflow-x-auto -mx-1">
        <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800 min-w-max mx-1">
          {tabs.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${activeTab === tab ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            >{tab}</button>
          ))}
        </div>
      </div>

      {activeTab === 'Profile' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 max-w-lg">
          <h3 className="text-lg font-semibold text-white mb-4">Profile</h3>
          {!user ? (
            <p className="text-slate-400 text-sm">Not signed in.</p>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Name</label>
                <input
                  type="text"
                  defaultValue={user.name ?? ''}
                  placeholder="Your name"
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Email</label>
                <input
                  type="email"
                  value={user.email}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white"
                  disabled
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Role</label>
                <input
                  type="text"
                  value={user.role ?? ''}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white capitalize"
                  disabled
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Tenant ID</label>
                <input
                  type="text"
                  value={user.tenantId}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-slate-500 font-mono"
                  disabled
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Timezone</label>
                <select className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white">
                  <option>America/Los_Angeles (PST)</option>
                  <option>America/New_York (EST)</option>
                  <option>America/Chicago (CST)</option>
                </select>
              </div>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium">Save Changes</button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'Team' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
          <div className="text-4xl mb-4">👥</div>
          <h3 className="text-lg font-semibold text-white mb-2">Team Management</h3>
          <p className="text-slate-400 mb-4">Invite team members and manage roles.</p>
          <p className="text-slate-500 text-xs">Team management coming soon.</p>
        </div>
      )}

      {activeTab === 'Integrations' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {mockIntegrations.map(integration => (
            <div key={integration.name} className="bg-slate-900 border border-slate-800 rounded-xl p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-3xl">{integration.icon}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-white">{integration.name}</h3>
                    <StatusDot status={integration.status} />
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    {integration.status === 'connected'
                      ? `${integration.accounts} account${integration.accounts > 1 ? 's' : ''} · Last sync: ${integration.lastSync}`
                      : 'Not configured'}
                  </div>
                </div>
              </div>
              <button className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                integration.status === 'connected' ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-blue-600 text-white hover:bg-blue-500'
              }`}>
                {integration.status === 'connected' ? 'Configure' : 'Connect'}
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'Billing' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Current Plan</h3>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl font-bold text-white">Enterprise</span>
              <span className="text-sm text-slate-400">Custom</span>
            </div>
            <div className="text-sm text-slate-400 mb-4">Unlimited modules, unlimited users, priority support</div>
            <button className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm">Manage Billing</button>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Usage This Month</h3>
            <p className="text-slate-500 text-sm">Usage metrics coming soon.</p>
          </div>
        </div>
      )}

      {activeTab === 'Sync Status' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-800">
            <h3 className="text-lg font-semibold text-white">Pipeline Health</h3>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Job</th>
                <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Schedule</th>
                <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Last Run</th>
                <th className="text-left p-4 text-xs font-medium text-slate-400 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {mockSyncJobs.map(job => (
                <tr key={job.name} className="border-b border-slate-800/50">
                  <td className="p-4 text-sm font-medium text-white">{job.name}</td>
                  <td className="p-4 text-sm text-slate-400">{job.schedule}</td>
                  <td className="p-4 text-sm text-slate-400">{job.lastRun}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <StatusDot status={job.status} />
                      <span className="text-xs text-slate-300">{job.status}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
